/**
 * Recognize shell commands that are exact stand-ins for the native `Read` and
 * `Write` tools, so the chat timeline can give them the same affordances
 * (file-preview chip, diff card, styled read card) instead of rendering them as
 * an opaque `Ran <command>` row.
 *
 * The bar is deliberately high: a command only qualifies when it has *no*
 * observable effect beyond reading one file, or replacing/extending one file's
 * contents. Anything that could expand, glob, chain, or otherwise do more than
 * the native tool bails out to `null`, and the row keeps its plain shell
 * rendering. It is always better to under-detect than to label a command as
 * something it isn't.
 *
 * "No observable effect" is the whole test for a write, and it has two halves:
 * the command must touch no file of its own (the shell's redirection, or the
 * one file `tee` names, is the only thing that opens one), and nothing may be
 * left on the terminal (each of stdout and stderr either goes to that file,
 * goes to `/dev/null`, or is a stream the command never writes). Purity isn't
 * derivable from a command's name, so it comes from an allowlist — see
 * {@link PURE_PRINTERS}. Whether the bytes are *knowable* is a separate, weaker
 * question: `echo hi > f` carries them, `date > f` doesn't, and both are still
 * writes.
 *
 * `tee` is the one command that may keep writing to the terminal, because that
 * copy is by definition the bytes the file got. That is what makes a two-stage
 * `printer | tee file` pipeline a write too — see {@link classifyPipeline} —
 * however its redundant stdout is then disposed of, or not.
 *
 * Shell syntax is not something to hand-roll, so the command is parsed into a
 * real bash AST by `mvdan-sh` (see {@link ./shell-parser.ts}). That is what
 * makes the "no observable effect" test tractable: an expansion, substitution,
 * or process substitution is a *distinct node type*, so a word qualifies only
 * when every one of its parts is a literal or a quoted literal — we never have
 * to enumerate the ways a `$` can change meaning. Only the parts of shell
 * behaviour the parser can't know about are left to us: whether a given
 * command's stdout is exactly a file's bytes, and the two expansions bash
 * performs on plain literals (globs and `~`).
 *
 * One assumption is unavoidable: a command carries no record of the shell it
 * ran in, so this reads it as default, non-interactive bash. A session that has
 * turned on `xpg_echo` (making `echo` interpret escapes) or `noclobber` (making
 * `> file` fail), or a harness running commands through dash, could make a
 * command mean something other than what it says.
 */
import type sh from 'mvdan-sh';
import { unwrapShellCommand } from './turn-blocks/shell-command.js';
import { getShellSyntax, redirOperator } from './shell-parser.js';

type Syntax = typeof sh.syntax;
type Node = Parameters<Syntax['NodeType']>[0];
type Stmt = NonNullable<ReturnType<ReturnType<Syntax['NewParser']>['Parse']>['Stmts'][number]>;

/**
 * A shell command whose only effect is to replace (`write`) or extend
 * (`append`) one file's contents.
 */
export interface ShellWriteOp {
  op: 'write' | 'append';
  path: string;
  /**
   * The exact bytes the command puts in the file, when the command's own text
   * carries them (`echo`, `printf`, a quoted heredoc). Undefined when the
   * command only *promises* to write one file and nothing else — `date > f`
   * touches nothing but `f`, but what lands in it isn't in the command — so the
   * row names the verb and the file and shows no diff.
   */
  content?: string;
}

/** A shell command that prints one file's contents verbatim. */
export interface ShellReadOp {
  op: 'read';
  path: string;
  /** The command's stdout already carries `cat -n` style line numbers. */
  numbered: boolean;
  /** 1-based file line number of the first line the command prints. */
  startLine: number;
}

export type ShellFileOp = ShellWriteOp | ShellReadOp;

/**
 * These paths are devices and kernel interfaces, not files the user can
 * preview: writing is a side effect and reading may not even be reproducible.
 * They keep the plain shell rendering. `/dev/null` is the one exception, and it
 * never reaches here — see {@link DISCARD_TARGET}.
 */
const NON_FILE_TARGETS = /^\/(dev|proc|sys)\//;

/**
 * The one redirect target that isn't a file and loses nothing by not being
 * shown: output sent here is output the reader was never going to see, so a
 * command that silences a stream this way is still a write of the other one.
 */
const DISCARD_TARGET = '/dev/null';

/**
 * Redirect destinations that put a stream back on a terminal stream instead of
 * in a file: writing here is not a file effect, and not showing it loses
 * nothing that `tee`'s guaranteed-identical copy doesn't already cover. A
 * command whose *only* output goes to one of these has written no file, so it
 * still isn't a write — see {@link classify}.
 */
const TERMINAL_TARGETS = /^\/dev\/(?:stdout|stderr|fd\/[12])$/;

/** Heredoc bodies can be large, but a whole repo file pasted in is not a UI concern. */
const MAX_COMMAND_LENGTH = 200_000;

/**
 * Expansions bash performs on an unquoted *literal*, which the AST therefore
 * can't flag for us: pathname (glob) and brace expansion, and `~`. Their
 * presence means the word on screen is not the final argument.
 */
const EXPANDING_LITERAL = /[*?[\]{}~]/;

/* -------------------------------------------------------------------------- */
/* AST helpers                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The redirection operators this module accepts, recovered from the parser by
 * {@link redirOperator} because the JS build exposes the enum only as opaque
 * numbers. Everything else stays unrecognized and is rejected.
 */
interface RedirOps {
  out: number;
  append: number;
  /** `>&N` — duplicate a stream onto another descriptor. */
  dupOut: number;
  /** `&>` — send both stdout and stderr to one destination. */
  allOut: number;
  heredoc: number;
  heredocDash: number;
}

function redirOps(syntax: Syntax): RedirOps {
  return {
    out: redirOperator(syntax, 'x > f'),
    append: redirOperator(syntax, 'x >> f'),
    dupOut: redirOperator(syntax, 'x >&1'),
    allOut: redirOperator(syntax, 'x &> f'),
    heredoc: redirOperator(syntax, "x <<'E'\nE"),
    heredocDash: redirOperator(syntax, "x <<-'E'\nE"),
  };
}

/**
 * `|` as distinct from every other statement operator, recovered the same way:
 * `BinCmdOperator` is a `const enum` too. `|&` (which pipes stderr as well) has
 * its own value and so never passes for `|`.
 */
const pipeOpCache = new WeakMap<object, number>();

function pipeOp(syntax: Syntax): number {
  const cached = pipeOpCache.get(syntax);
  if (cached !== undefined) return cached;
  const cmd = syntax.NewParser().Parse('a | b', 'probe.sh').Stmts[0]?.Cmd;
  const op =
    cmd && syntax.NodeType(cmd) === 'BinaryCmd' ? (cmd as unknown as { Op: number }).Op : -1;
  pipeOpCache.set(syntax, op);
  return op;
}

/**
 * The exact text a word contributes to argv, or `null` when it isn't knowable
 * from the source: every part must be a literal, a single-quoted string, or a
 * double-quoted run of literals. An expansion or substitution is a different
 * node type and so never survives this.
 */
function literalWord(syntax: Syntax, word: Node | null): string | null {
  if (!word) return null;
  const parts = (word as { Parts?: Node[] }).Parts;
  if (!parts) return null;
  let text = '';
  for (const part of parts) {
    const value = literalPart(syntax, part);
    if (value === null) return null;
    text += value;
  }
  return text;
}

function literalPart(syntax: Syntax, part: Node): string | null {
  const node = part as { Value?: string; Dollar?: boolean; Parts?: Node[] };
  switch (syntax.NodeType(part)) {
    case 'Lit': {
      const value = node.Value ?? '';
      // A backslash is an escape bash removes, so the literal text and the
      // source text diverge; globs and `~` mean the argument isn't final.
      if (value.includes('\\') || EXPANDING_LITERAL.test(value)) return null;
      return value;
    }
    case 'SglQuoted':
      // `$'…'` is an ANSI-C string whose escapes we would have to interpret.
      return node.Dollar ? null : (node.Value ?? '');
    case 'DblQuoted': {
      // `$"…"` is a locale translation, and any non-literal part expands.
      if (node.Dollar) return null;
      let text = '';
      for (const inner of node.Parts ?? []) {
        if (syntax.NodeType(inner) !== 'Lit') return null;
        const value = (inner as { Value?: string }).Value ?? '';
        if (value.includes('\\')) return null;
        text += value;
      }
      return text;
    }
    default:
      return null;
  }
}

/**
 * The bytes a heredoc feeds to the command. A quoted delimiter (`<<'EOF'`)
 * means bash copies the body verbatim. An unquoted one means it is expanded —
 * usually visible as a non-literal part, but backslash escapes and line
 * continuations are processed too, so a body carrying either is refused.
 */
function heredocBody(syntax: Syntax, redirect: Node, stripTabs: boolean): string | null {
  const { Word: delimiter, Hdoc: hdoc } = redirect as unknown as {
    Word: Node | null;
    Hdoc: Node | null;
  };
  if (!delimiter) return null;
  if (!hdoc) return ''; // a heredoc whose body is empty carries no word at all
  const delimiterQuoted = (delimiter as { Parts?: Node[] }).Parts?.some((part) => {
    const type = syntax.NodeType(part);
    return type === 'SglQuoted' || type === 'DblQuoted';
  });

  let body = '';
  for (const part of (hdoc as { Parts?: Node[] }).Parts ?? []) {
    if (syntax.NodeType(part) !== 'Lit') return null; // an expansion in the body
    const value = (part as { Value?: string }).Value ?? '';
    if (!delimiterQuoted && value.includes('\\')) return null;
    body += value;
  }
  // `<<-` strips leading tabs from every line, including the closing delimiter.
  return stripTabs ? body.replace(/^\t+/gm, '') : body;
}

/* -------------------------------------------------------------------------- */
/* Command semantics                                                           */
/* -------------------------------------------------------------------------- */

/** `printf` escapes we can reproduce exactly; anything else disqualifies. */
const PRINTF_ESCAPES: Record<string, string> = {
  n: '\n',
  t: '\t',
  r: '\r',
  '\\': '\\',
};

function unescapePrintfFormat(format: string): string | null {
  let out = '';
  for (let i = 0; i < format.length; i += 1) {
    const ch = format[i];
    if (ch === '%') return null; // a conversion spec with no argument we can resolve
    if (ch !== '\\') {
      out += ch;
      continue;
    }
    const next = format[i + 1];
    const replacement = next === undefined ? undefined : PRINTF_ESCAPES[next];
    if (replacement === undefined) return null;
    out += replacement;
    i += 1;
  }
  return out;
}

/** Content produced on stdout by an `echo`/`printf` command, or `null`. */
function stdoutOfPrinter(argv: string[]): string | null {
  const [name, ...args] = argv;

  if (name === 'echo') {
    // bash's echo takes `-n`, `-e` and `-E` in any combination and stops at the
    // first argument that isn't one of them — which is then text, dash and all.
    // That is what lets a markdown bullet (`echo '- item'`) through.
    let newline = true;
    let i = 0;
    for (; i < args.length; i += 1) {
      const flags = /^-([neE]+)$/.exec(args[i])?.[1];
      if (!flags) break;
      // `-e` interprets escapes, so the bytes on stdout stop being the bytes
      // written here. (`-E` restores the default and changes nothing.)
      if (flags.includes('e')) return null;
      if (flags.includes('n')) newline = false;
    }
    return args.slice(i).join(' ') + (newline ? '\n' : '');
  }

  if (name === 'printf') {
    const [format, ...rest] = args;
    if (format === undefined) return null;
    // `printf -n` is an invalid option, not a format: the command fails and
    // writes nothing.
    if (format.startsWith('-')) return null;
    if (format === '%s') return rest.length === 1 ? rest[0] : null;
    if (format === '%s\\n' || format === '%s\n') return rest.length === 1 ? rest[0] + '\n' : null;
    return rest.length === 0 ? unescapePrintfFormat(format) : null;
  }

  return null;
}

/** A command called with no arguments at all. */
const noArgs = (args: string[]) => args.length === 0;

/** A command whose every argument is a flag we recognize. */
const flagsOnly = (allowed: RegExp) => (args: string[]) => args.every((arg) => allowed.test(arg));

/** A command called with operands only — no flag to change what it prints. */
const operandsOnly = (args: string[]) => args.every((arg) => !arg.startsWith('-'));

/**
 * Commands whose whole job is to print something: they open no file, change
 * nothing on disk, and stay silent on stderr when they succeed. Redirected to a
 * file, such a command *is* a write — the bytes simply aren't in the command
 * text, so {@link ShellWriteOp.content} stays undefined and the row shows no
 * diff.
 *
 * Purity is a property of a program, not of its name, so this is an allowlist
 * rather than a heuristic, and each entry carries the arguments that keep the
 * guarantee: `date -r f` reports a file's mtime and `env CMD` runs CMD, so
 * neither is the command its name suggests. Anything outside an entry's policy
 * is a command this module doesn't claim to know.
 */
const PURE_PRINTERS: Record<string, (args: string[]) => boolean> = {
  // Clock, host, and identity. `date`'s `+FORMAT` is a format string, not a
  // path, but `-r`'s argument is a file and `-d`/`-f` take input of their own.
  date: (args) =>
    args.every((arg) => arg.startsWith('+') || /^(?:-[uR]|--utc|--universal)$/.test(arg)),
  pwd: flagsOnly(/^-[LP]$/),
  hostname: noArgs,
  uname: flagsOnly(/^(?:-[asnrvmpio]+|--all)$/),
  uptime: noArgs,
  nproc: noArgs,
  arch: noArgs,
  whoami: noArgs,
  groups: noArgs,
  // An operand would look up a different user; the flags only reformat.
  id: flagsOnly(/^-[ugGnr]+$/),
  // The environment is memory, not disk — but `env` with any argument runs a
  // command, which is the one thing this table must never wave through.
  env: noArgs,
  // `printenv NAME` exits 1 when NAME is not set, so we cannot know at parse
  // time whether a named-variable form will succeed. Restrict to no-args (print
  // all variables), which always succeeds and writes nothing of its own.
  printenv: noArgs,
  // Pure text over their own arguments. `basename`/`dirname` take path-shaped
  // operands but never open them.
  seq: (args) =>
    args.length > 0 &&
    args.every((arg) => /^-?\d+(?:\.\d+)?$/.test(arg)) &&
    // GNU seq exits 1 for a zero increment: `seq 1 0 5` writes nothing to the file.
    !(args.length === 3 && parseFloat(args[1]) === 0),
  // GNU basename accepts at most 2 positional arguments (NAME and optional SUFFIX).
  basename: (args) => args.length >= 1 && args.length <= 2 && operandsOnly(args),
  // GNU dirname accepts exactly 1 positional argument.
  dirname: (args) => args.length === 1 && operandsOnly(args),
};

/** What a command leaves on its streams, given that it opens no file itself. */
interface StreamOutput {
  /** The exact stdout bytes, when the command's own text carries them. */
  stdout?: string;
  /** True when the command puts nothing on stdout at all. */
  quiet?: boolean;
}

/**
 * A command that only prints, or `null` for one that could do anything else.
 * This is the whole "no observable effect" test on the command side; the
 * redirects are checked separately.
 */
function outputOnlyCommand(argv: string[]): StreamOutput | null {
  // No command at all is a bare redirect: the shell creates or truncates the
  // file and nothing runs.
  if (argv.length === 0) return { stdout: '', quiet: true };
  const [name, ...args] = argv;
  // `:` and `true` do nothing and print nothing, which makes the redirect the
  // whole command: `: > f` truncates `f`.
  if (args.length === 0 && (name === ':' || name === 'true')) return { stdout: '', quiet: true };
  const stdout = stdoutOfPrinter(argv);
  if (stdout !== null) return { stdout };
  // An `echo`/`printf` whose output we couldn't reproduce falls through to a
  // table that deliberately doesn't list them, and so is refused.
  return PURE_PRINTERS[name]?.(args) ? {} : null;
}

/**
 * Split argv into flags and operands, honouring the `--` end-of-options
 * marker and the value-taking flags a caller declares. Returns `null` when an
 * operand looks like a flag we didn't account for, so an unrecognized option
 * can never be mistaken for the file being read.
 */
function splitArgs(
  args: string[],
  valueFlags: ReadonlySet<string>,
): { flags: string[]; operands: string[] } | null {
  const flags: string[] = [];
  const operands: string[] = [];
  let optionsEnded = false;
  for (let i = 0; i < args.length; i += 1) {
    const arg = args[i];
    if (!optionsEnded && arg === '--') {
      optionsEnded = true;
      continue;
    }
    if (!optionsEnded && arg.startsWith('-') && arg.length > 1) {
      flags.push(arg);
      if (valueFlags.has(arg)) {
        const value = args[i + 1];
        if (value === undefined) return null;
        flags.push(value);
        i += 1;
      }
      continue;
    }
    if (!optionsEnded && arg.startsWith('-')) return null; // bare `-` is stdin
    operands.push(arg);
  }
  return { flags, operands };
}

/**
 * `sed -n '12,40p'` — the one sed form that is a plain ranged read. Line
 * addresses are 1-based; `0` is an error in sed, not a read.
 */
const SED_RANGE = /^([1-9]\d*)(?:,(?:\d+|\$))?p$/;
/** `head -n 50`, `head -n50`, `head -50` — a prefix of the file. */
const HEAD_COUNT = /^-(n\d+|\d+)$/;

/** An empty path is an error in every shell, and would render a row with no subject. */
function isFilePath(path: string): boolean {
  return path.length > 0 && !NON_FILE_TARGETS.test(path);
}

function readOp(path: string, numbered: boolean, startLine = 1): ShellReadOp | null {
  return isFilePath(path) ? { op: 'read', path, numbered, startLine } : null;
}

function writeOp(path: string, append: boolean, content?: string): ShellWriteOp | null {
  if (!isFilePath(path)) return null;
  const op = append ? 'append' : 'write';
  // Omit `content` rather than carrying an undefined key, so "we know the bytes"
  // and "we don't" are distinguishable by presence.
  return content === undefined ? { op, path } : { op, path, content };
}

/**
 * Read-equivalent commands: exactly one file operand and no flag that
 * transforms the bytes. `head` and `sed -n 'A,Bp'` qualify because the native
 * Read tool takes the same offset/limit slice of the file.
 */
function parseRead(argv: string[]): ShellReadOp | null {
  const [name, ...rawArgs] = argv;

  if (name === 'cat') {
    const split = splitArgs(rawArgs, new Set());
    if (!split || split.operands.length !== 1) return null; // stdin, or a concatenation
    if (split.flags.length === 0) return readOp(split.operands[0], false);
    // `-n` numbers every line, exactly like the Read tool's own output.
    if (split.flags.length === 1 && split.flags[0] === '-n') {
      return readOp(split.operands[0], true);
    }
    return null;
  }

  if (name === 'head') {
    const split = splitArgs(rawArgs, new Set(['-n']));
    if (!split || split.operands.length !== 1) return null;
    const { flags } = split;
    const ok =
      flags.length === 0 ||
      (flags.length === 1 && HEAD_COUNT.test(flags[0])) ||
      (flags.length === 2 && flags[0] === '-n' && /^\d+$/.test(flags[1]));
    return ok ? readOp(split.operands[0], false) : null;
  }

  if (name === 'sed') {
    // `-n` plus a print script: the script is an operand as far as splitArgs
    // is concerned, so the file is the second one.
    const split = splitArgs(rawArgs, new Set());
    if (!split || split.flags.length !== 1 || split.flags[0] !== '-n') return null;
    if (split.operands.length !== 2) return null;
    const match = SED_RANGE.exec(split.operands[0]);
    if (!match) return null;
    return readOp(split.operands[1], false, Number(match[1]));
  }

  return null;
}

/* -------------------------------------------------------------------------- */
/* Entry points                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Classify a shell command as an exact read, write, or append of one file, or
 * `null` when it does anything else — including when the command doesn't parse
 * at all (an unterminated quote, an unclosed heredoc).
 */
export function parseShellFileOp(command: string, syntax = getShellSyntax()): ShellFileOp | null {
  if (!syntax || command.length === 0 || command.length > MAX_COMMAND_LENGTH) return null;
  try {
    return classify(syntax, command);
  } catch {
    // A syntax error (an unterminated quote, an unclosed heredoc) is not a file
    // op, and no shape the parser hands back should be able to fail a chat row.
    return null;
  }
}

function classify(syntax: Syntax, command: string): ShellFileOp | null {
  const { Stmts: stmts } = syntax
    .NewParser(syntax.Variant(syntax.LangBash))
    .Parse(command, 'command.sh');
  // Two statements are two commands, whatever the second one does.
  const stmt = stmts.length === 1 ? stmts[0] : undefined;
  if (!stmt) return null;

  // A pipeline is the one compound command with a stand-in form: everything
  // else (`&&` chain, subshell, loop, function) is a different node type and
  // stops here.
  const cmd = stmt.Cmd as Node | null;
  if (cmd && syntax.NodeType(cmd) === 'BinaryCmd') {
    // `&`, `!` and `coproc` attach to the pipeline as a whole, so the stages'
    // own check doesn't see them.
    if (!statementFlagsPlain(stmt)) return null;
    // Redirections on the pipeline itself would have to be modelled against the
    // stage they attach to; bash puts them on the stages, so there are none.
    if ((stmt.Redirs as unknown as Node[]).length > 0) return null;
    return classifyPipeline(syntax, cmd);
  }

  const argv = statementArgv(syntax, stmt);
  if (!argv) return null;

  const plan = redirectPlan(syntax, stmt.Redirs as unknown as Node[]);
  if (!plan) return null;
  const { target, discarded, terminal, heredoc } = plan;

  if (heredoc !== null) {
    // `cat > file <<'EOF'`, `cat <<'EOF' > file`, `cat >> file <<'EOF'` — cat's
    // stdout is exactly the body, and it says nothing on stderr on success.
    if (argv.length === 1 && argv[0] === 'cat' && target?.stream === 'out') {
      return writeOp(target.path, target.append, heredoc);
    }
    // `tee file <<'EOF'` — the body is what tee writes.
    return teeWrite(argv, plan, heredoc);
  }

  if (target) {
    const output = outputOnlyCommand(argv);
    if (!output) return null;
    // Nothing may be left on the terminal. stderr needs no discard: every
    // command that gets this far is silent on it when it succeeds, and when it
    // fails the row says so, which is the truth. stdout is louder — if the file
    // is capturing stderr instead, stdout must have gone to `/dev/null` or
    // never been written at all.
    if (target.stream === 'err' && !output.quiet && !discarded.has('out')) return null;
    // Only the captured stream's bytes reach the file. stderr's are whatever
    // the command chose to complain about, which the command text never says.
    return writeOp(target.path, target.append, target.stream === 'out' ? output.stdout : undefined);
  }

  // A read's stdout *is* the thing the row shows, so it may not be sent
  // anywhere else — that command reads nothing anyone can see here. A silenced
  // or aliased stderr changes none of it.
  if (discarded.has('out') || terminal.has('out')) return null;
  return parseRead(argv);
}

/**
 * `printer | tee [-a] file` — the pipeline form of a write. The left stage
 * supplies the bytes and must be output-only, exactly as it would be if it were
 * redirecting to the file itself; `tee` then writes them to the one file it
 * names.
 */
function classifyPipeline(syntax: Syntax, cmd: Node): ShellFileOp | null {
  const bin = cmd as unknown as { Op: number; X: Stmt; Y: Stmt };
  // `|&` pipes stderr into tee as well, so what lands in the file is no longer
  // just the stdout this module can account for.
  if (bin.Op !== pipeOp(syntax)) return null;

  const source = pipelineSource(syntax, bin.X);
  if (!source) return null;

  const argv = statementArgv(syntax, bin.Y);
  if (!argv) return null;
  const plan = redirectPlan(syntax, bin.Y.Redirs as unknown as Node[]);
  // A heredoc on tee would take over the stdin the pipe just supplied, leaving
  // two claims on one stream and nothing worth reporting.
  if (!plan || plan.heredoc !== null) return null;
  return teeWrite(argv, plan, source.stdout);
}

/**
 * What the left stage of the pipeline puts on the pipe, or `null` when that
 * isn't exactly one command's stdout. `stdout` is undefined when the command is
 * output-only but its bytes aren't in the command text (`date | tee f`).
 */
function pipelineSource(syntax: Syntax, stmt: Stmt): StreamOutput | null {
  const argv = statementArgv(syntax, stmt);
  if (!argv) return null;
  const plan = redirectPlan(syntax, stmt.Redirs as unknown as Node[]);
  if (!plan) return null;
  // Anything that takes stdout away from the pipe (a file, a discard, another
  // descriptor) means the bytes reaching tee are not the ones we just read.
  if (plan.target || plan.discarded.has('out') || plan.terminal.has('out')) return null;
  // `cat <<'EOF' | tee file` — the heredoc body is the stdout.
  if (plan.heredoc !== null) {
    return argv.length === 1 && argv[0] === 'cat' ? { stdout: plan.heredoc } : null;
  }
  return outputOnlyCommand(argv);
}

/**
 * `tee [-a] file` — the one command allowed to go on writing to the terminal,
 * because that copy is guaranteed to be the bytes the file got. The file is an
 * operand, so a redirect here may only dispose of that copy (`> /dev/null`,
 * `>&2`, `> /dev/stdout`), never name a second file, and `-a` is what makes the
 * write an append.
 */
function teeWrite(argv: string[], plan: RedirectPlan, content?: string): ShellWriteOp | null {
  if (argv[0] !== 'tee' || plan.target) return null;
  const split = splitArgs(argv.slice(1), new Set());
  if (!split || split.operands.length !== 1) return null;
  const appends = split.flags.filter((flag) => flag === '-a' || flag === '--append');
  if (appends.length !== split.flags.length) return null;
  return writeOp(split.operands[0], appends.length > 0, content);
}

/**
 * The literal argv of a statement's plain command, or `null` when the statement
 * is anything else. An empty argv is a bare redirect (`> file`), where the shell
 * opens the file and nothing runs.
 */
function statementArgv(syntax: Syntax, stmt: Stmt): string[] | null {
  if (!statementFlagsPlain(stmt)) return null;
  const cmd = stmt.Cmd as Node | null;
  if (!cmd) return [];
  if (syntax.NodeType(cmd) !== 'CallExpr') return null;
  const call = cmd as unknown as { Assigns: unknown[]; Args: Node[] };
  if (call.Assigns.length > 0) return null; // `FOO=1 cat file` changes the environment
  const argv: string[] = [];
  for (const word of call.Args) {
    const text = literalWord(syntax, word);
    if (text === null) return null;
    argv.push(text);
  }
  return argv;
}

/**
 * Whether a statement runs plainly: backgrounded (`&`) means the row can
 * complete before the file does, `!` inverts the status the row reports, and
 * `coproc` runs it on a pair of pipes nobody here is reading.
 */
function statementFlagsPlain(stmt: Stmt): boolean {
  const flags = stmt as unknown as { Background?: boolean; Negated?: boolean; Coprocess?: boolean };
  return !flags.Background && !flags.Negated && !flags.Coprocess;
}

/** Which of the two output streams a redirect operates on. */
type Stream = 'out' | 'err';

/** Where the shell sends a command's output, and what it feeds it on stdin. */
interface RedirectPlan {
  /** The one real file the command writes, and how it is opened. */
  target: { path: string; append: boolean; stream: Stream } | null;
  /** Streams whose output was thrown away at {@link DISCARD_TARGET}. */
  discarded: Set<Stream>;
  /** Streams sent to another terminal stream — see {@link TERMINAL_TARGETS}. */
  terminal: Set<Stream>;
  /** Bytes a heredoc feeds on stdin, or `null` when there is no heredoc. */
  heredoc: string | null;
}

/**
 * Resolve a statement's redirections into at most one file, any discarded
 * streams, and any heredoc — or `null` for a redirection whose effect this
 * module doesn't model.
 */
function redirectPlan(syntax: Syntax, redirs: readonly Node[]): RedirectPlan | null {
  const ops = redirOps(syntax);
  const plan: RedirectPlan = {
    target: null,
    discarded: new Set(),
    terminal: new Set(),
    heredoc: null,
  };
  const redirected = new Set<Stream>();
  // The same stream redirected twice keeps only the last — after the first file
  // has already been truncated.
  const claim = (stream: Stream | null) => {
    if (!stream || redirected.has(stream)) return false;
    redirected.add(stream);
    return true;
  };

  for (const redir of redirs) {
    const node = redir as unknown as { Op: number; N: Node | null; Word: Node | null };
    if (node.Op === ops.heredoc || node.Op === ops.heredocDash) {
      if (plan.heredoc !== null) return null;
      plan.heredoc = heredocBody(syntax, redir, node.Op === ops.heredocDash);
      if (plan.heredoc === null) return null;
      continue;
    }
    if (node.Op === ops.allOut) {
      // `&> /dev/null` throws both streams away. `&> file` would interleave two
      // streams into one file, so the bytes stop being knowable — and `&>>` is
      // an operator away from this one, so it stays unrecognized.
      if (literalWord(syntax, node.Word) !== DISCARD_TARGET) return null;
      if (!claim('out') || !claim('err')) return null;
      plan.discarded.add('out');
      plan.discarded.add('err');
      continue;
    }
    if (node.Op === ops.dupOut) {
      // `>&2`, `2>&1` — the stream joins the other terminal stream. `>&-` closes
      // it instead, which makes the command's writes fail, so only 1 and 2 pass.
      const word = literalWord(syntax, node.Word);
      if (word !== '1' && word !== '2') return null;
      const stream = redirectedStream(syntax, node.N);
      if (!claim(stream) || !stream) return null;
      plan.terminal.add(stream);
      continue;
    }
    // `>|` overrides noclobber, and `<`, `<<<`, `<>` all mean something this
    // module says nothing about.
    if (node.Op !== ops.out && node.Op !== ops.append) return null;
    const stream = redirectedStream(syntax, node.N);
    if (!claim(stream) || !stream) return null;
    const path = literalWord(syntax, node.Word);
    if (path === null) return null;
    if (path === DISCARD_TARGET) {
      plan.discarded.add(stream);
      continue;
    }
    if (TERMINAL_TARGETS.test(path)) {
      plan.terminal.add(stream);
      continue;
    }
    if (plan.target) return null; // two files is two effects
    plan.target = { path, append: node.Op === ops.append, stream };
  }
  // A file plus a descriptor alias (`date > f 2>&1`) makes the file's bytes
  // depend on which redirect the shell applied first. Not worth modelling: a
  // terminal disposal only counts when no file is being written.
  if (plan.target && plan.terminal.size > 0) return null;
  return plan;
}

/**
 * The stream a redirect's file descriptor names: stdout for a bare `>` or `1>`,
 * stderr for `2>`. Any other descriptor is a channel we can say nothing about.
 */
function redirectedStream(syntax: Syntax, fd: Node | null): Stream | null {
  if (!fd) return 'out';
  if (syntax.NodeType(fd) !== 'Lit') return null;
  const value = (fd as { Value?: string }).Value;
  return value === '1' ? 'out' : value === '2' ? 'err' : null;
}

/**
 * Convenience wrapper for tool-call inputs: returns the file op when the call
 * is a shell command that stands in for Read/Write. Agents that shell out
 * report the argv they ran (`/bin/bash -lc "…"`), so the inner command is
 * unwrapped before classification.
 */
export function shellFileOpFromInput(
  input: Record<string, unknown> | null,
  syntax = getShellSyntax(),
): ShellFileOp | null {
  const raw = input?.command;
  if (typeof raw !== 'string' || raw.length === 0) return null;
  return parseShellFileOp(unwrapShellCommand(raw), syntax);
}

/** The native Read tool's own line ceiling. */
const MAX_NUMBERED_LINES = 2000;

/**
 * A harness notice standing in for output it dropped, e.g.
 * `[... output truncated: 40000 characters omitted ...]`. Everything after one
 * sits at a line number we can't know, so the gutter would be a lie.
 */
const TRUNCATION_NOTICE = /^\s*[[(][^\])]*\b(?:truncated|omitted|elided)\b[^\])]*[\])]\s*$/im;

export interface NumberedLine {
  lineNo: number;
  content: string;
}

/**
 * Number raw file text so it renders through the styled Read card like a
 * numbered Read result. Used for shell reads (`cat file`, `head -n 40 file`)
 * whose stdout is the file's bytes with no gutter of its own; `startLine` is
 * the file line the output begins at, so a ranged read stays honest about
 * where it sits in the file.
 *
 * Returns `null` when the gutter would be untrustworthy — nothing to number, or
 * output a harness has visibly cut down — so the plain output panel takes over.
 */
export function numberLines(text: string, startLine: number): NumberedLine[] | null {
  if (text.length === 0 || TRUNCATION_NOTICE.test(text)) return null;
  const lines = text.replace(/\n$/, '').split('\n');
  return capReadLines(lines.map((content, i) => ({ lineNo: startLine + i, content })));
}

/**
 * Unlike the native Read tool, a shell read has no output limit of its own, and
 * the styled card renders and highlights every line eagerly. Past the ceiling
 * the plain output panel is the cheaper view.
 */
export function capReadLines(lines: NumberedLine[] | null): NumberedLine[] | null {
  return lines && lines.length <= MAX_NUMBERED_LINES ? lines : null;
}
